import { Loader } from '@googlemaps/js-api-loader'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

export type AddressOption = {
  id?: string | number
  label: string
  value: string
  formattedAddress: string
  lat?: number
  lng?: number
  components?: {
    street_number?: string
    route?: string
    locality?: string
    postal_code?: string
    country?: string
    country_code?: string
  }
}

// Get Google Maps API key
const getGoogleMapsApiKey = (): string => {
  return import.meta.env.VITE_GOOGLE_PLACES_API_KEY || ''
}

// Create a Google Maps loader instance
const loader = new Loader({
  apiKey: getGoogleMapsApiKey(),
  version: 'weekly',
  libraries: ['places']
})

// Search addresses using Google Places Autocomplete
const searchAddresses = async (query: string): Promise<AddressOption[]> => {
  if (!query || query.length < 3) return []

  try {
    // Make sure the places library is loaded
    await loader.importLibrary('places')

    // Create a session token for billing optimization
    const sessionToken = new google.maps.places.AutocompleteSessionToken()

    // Create the autocomplete service
    const service = new google.maps.places.AutocompleteService()

    // Define bounds for France (approximate center and bounds)
    const franceBounds = {
      north: 51.071002,
      south: 42.3332995,
      east: 8.1767166,
      west: -4.79034948348999
    }

    // Request predictions with France location bias (@todo better use user location)
    const response = await service.getPlacePredictions({
      input: query,
      types: ['address'],
      sessionToken,
      locationBias: franceBounds
    })

    if (!response || !response.predictions) {
      return []
    }

    // Transform predictions to match our AddressOption format
    return response.predictions.map((prediction) => ({
      label: prediction.description,
      value: prediction.place_id,
      formattedAddress: prediction.description,
      // These fields will be populated when an address is selected
      lat: undefined,
      lng: undefined,
      components: {}
    }))
  } catch (error) {
    console.error('Error fetching address suggestions:', error)
    return []
  }
}

// Get place details for a selected place
export const getPlaceDetails = async (
  placeId: string
): Promise<AddressOption | null> => {
  try {
    // Make sure the places library is loaded
    await loader.importLibrary('places')

    // Create a session token for billing optimization
    const sessionToken = new google.maps.places.AutocompleteSessionToken()

    // Create the places service (requires a DOM element, but we're just using it for the API call)
    const tempDiv = document.createElement('div')
    const service = new google.maps.places.PlacesService(tempDiv)

    // Create a promise wrapper for the getDetails method
    const getDetailsPromise = (
      request: google.maps.places.PlaceDetailsRequest
    ): Promise<google.maps.places.PlaceResult> => {
      return new Promise((resolve, reject) => {
        service.getDetails(request, (result, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && result) {
            resolve(result)
          } else {
            reject(
              new Error(`Place details request failed with status: ${status}`)
            )
          }
        })
      })
    }

    // Get the place details
    const place = await getDetailsPromise({
      placeId,
      fields: ['address_components', 'formatted_address', 'geometry'],
      sessionToken
    })

    if (!place || !place.address_components) {
      return null
    }

    // Extract address components
    const components: AddressOption['components'] = {}

    for (const component of place.address_components) {
      if (component.types.includes('street_number')) {
        components.street_number = component.long_name
      }
      if (component.types.includes('route')) {
        components.route = component.long_name
      }
      if (component.types.includes('locality')) {
        components.locality = component.long_name
      }
      if (component.types.includes('postal_code')) {
        components.postal_code = component.long_name
      }
      if (component.types.includes('country')) {
        components.country = component.long_name
        components.country_code = component.short_name
      }
    }

    return {
      label: place.formatted_address || '',
      value: placeId,
      formattedAddress: place.formatted_address || '',
      lat: place.geometry?.location?.lat(),
      lng: place.geometry?.location?.lng(),
      components
    }
  } catch (error) {
    console.error('Error fetching place details:', error)
    return null
  }
}

export const useGoogleAddressSearch = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false)

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMaps = async () => {
      try {
        await loader.importLibrary('places')
        setIsGoogleLoaded(true)
      } catch (error) {
        console.error('Error loading Google Maps API:', error)
      }
    }

    loadGoogleMaps()
  }, [])

  const {
    data: addresses = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['addressSearch', searchQuery],
    queryFn: () => searchAddresses(searchQuery),
    enabled: isGoogleLoaded && searchQuery.length >= 3,
    staleTime: 1000 * 60 * 5 // 5 minutes
  })

  return {
    addresses,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    isGoogleLoaded
  }
}
